import { SolIcon } from '@sol/icons';
import { ObjectTools } from '../../tools/object-tools';
import { SolText } from './sol-text';
import { SolButton, SolButtonType } from './sol-button';

export interface SolFile {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  file?: File;
}

export class SolFileUpload {
  private constructor(
    public readonly buttonText: SolText,
    public readonly buttonIcon: SolIcon,
    public readonly file: SolFile | undefined,
    public readonly files: readonly SolFile[],
    public readonly multiple: boolean,
    public readonly accept: string,
    public readonly disabled: boolean,
    public readonly hostClass: string,
    public readonly hasFile: boolean,
    public readonly button: SolButton,
    public readonly clearButton: SolButton,
    public readonly fileName: string,
  ) {}

  public static of(
    buttonText: SolText = SolText.of('sol-common.upload-file'),
    buttonIcon: SolIcon = SolIcon.upload,
  ): SolFileUpload {
    return new SolFileUpload(
      buttonText,
      buttonIcon,
      undefined,
      [],
      false,
      '',
      false,
      '',
      false,
      SolButton.of(),
      SolButton.of(),
      '',
    )
      .calculateHostClass()
      .calculateFileInfo()
      .calculateButtons();
  }

  public withButtonText(buttonText: SolText): SolFileUpload {
    return this.copy({ buttonText });
  }

  public withButtonIcon(buttonIcon: SolIcon): SolFileUpload {
    return this.copy({ buttonIcon }).calculateButtons();
  }

  public withFile(file: SolFile | undefined): SolFileUpload {
    return this.copy({ file, files: file !== undefined ? [file] : [] })
      .calculateHostClass()
      .calculateFileInfo();
  }

  public withFiles(files: readonly SolFile[]): SolFileUpload {
    return this.copy({ files, file: files[0] }).calculateHostClass().calculateFileInfo();
  }

  public withMultiple(multiple: boolean): SolFileUpload {
    return this.copy({ multiple }).calculateFileInfo();
  }

  public withAccept(accept: string): SolFileUpload {
    return this.copy({ accept });
  }

  public withDisabled(disabled: boolean): SolFileUpload {
    return this.copy({ disabled }).calculateHostClass().calculateButtons();
  }

  private calculateHostClass(): SolFileUpload {
    const hostClass =
      'sol-file-upload' +
      (this.file !== undefined ? ' sol-file-upload-has-file' : '') +
      (this.disabled ? ' sol-file-upload-disabled' : '');
    return this.copy({ hostClass });
  }

  private calculateFileInfo(): SolFileUpload {
    const hasFile = this.file !== undefined;
    const fileName = this.multiple ? this.files.map(file => file.name).join(', ') : (this.file?.name ?? '');
    return this.copy({ hasFile, fileName });
  }

  private calculateButtons(): SolFileUpload {
    const button = SolButton.of('', this.buttonIcon)
      .withType(SolButtonType.secondary)
      .withDisabled(this.disabled)
      .withCustom(true);
    const clearButton = SolButton.of('', SolIcon.remove).withType(SolButtonType.secondary).withDisabled(this.disabled);
    return this.copy({ button, clearButton });
  }

  private copy(props: Partial<SolFileUpload>): SolFileUpload {
    return ObjectTools.copy<SolFileUpload>(this, props);
  }
}
